import { type RefObject, useEffect, useRef } from "react";

/** Whether something is in a dialog, or its backdrop: a dialog in front owns presses and Escape. */
const inDialog = (target: EventTarget | null) =>
  target instanceof Element &&
  target.closest('[role="dialog"], [data-slot="dialog-overlay"]') !== null;

/**
 * The two ways out of an open panel: press Escape, or press somewhere else. Neither counts while
 * it happens in a dialog open in front, such as one the panel opened.
 *
 * Put the returned ref on the panel's outermost element, including its
 * trigger, so a press on the trigger counts as inside and the panel's own
 * toggle decides what happens.
 *
 * When the panel is painted elsewhere, pass the trigger so a press on it
 * is still inside.
 */
export function usePanelDismiss<Element extends HTMLElement>({
  open,
  onDismiss,
  dismissOnOutsidePress = true,
  trigger,
}: {
  open: boolean;
  onDismiss: () => void;
  /** Turn off where something else already owns pointer presses, like the map. */
  dismissOnOutsidePress?: boolean;
  trigger?: RefObject<HTMLElement | null>;
}) {
  const panelRef = useRef<Element>(null);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement;

    const dismissOnPressOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || trigger?.current?.contains(target)) return;
      if (inDialog(event.target)) return;
      onDismiss();
    };

    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || inDialog(event.target)) return;
      // Hand the keyboard back before the panel goes away. A panel whose
      // trigger is the panel itself has nothing to return to, so this is
      // skipped rather than guessed at.
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
      onDismiss();
    };

    // Captured, so a press that something else stops from bubbling, like a
    // dialog's or a map's own handling, still counts as outside.
    if (dismissOnOutsidePress)
      document.addEventListener("pointerdown", dismissOnPressOutside, { capture: true });
    document.addEventListener("keydown", dismissOnEscape);

    return () => {
      document.removeEventListener("pointerdown", dismissOnPressOutside, { capture: true });
      document.removeEventListener("keydown", dismissOnEscape);
    };
  }, [open, onDismiss, dismissOnOutsidePress, trigger]);

  return panelRef;
}
